import type { EReportChart, IRMetricKey } from '~/presentation/reports/common/type';
import type { OrderByFields } from '~/presentation/reports/ui/ReportTable/types';
import type { AnalyticInterval } from '~/modules/analytics/types';

import {
  EControlSettingType,
  EDimensionKey,
  EReportControlId,
} from '~/presentation/reports/common/type';
import {
  getAllControlIds,
  getControlIdsById,
  getControlValueById,
} from '~/presentation/reports/common';
import { useControlSettingStore } from '~/presentation/reports/common/ReportFilter/stores';
import { EOrderDirectionType } from '~/presentation/reports/ui/ReportTable/types';
import { useReportControlStore } from '../stores';
import { useEffect, useMemo } from 'react';
import { TIME_KEYS } from '../constants';

export const useGetValues = () => {
  const orderBy = useReportControlStore((state) => state.orderBy);
  const changeOrderBy = useReportControlStore((state) => state.changeOrderBy);
  const addOrderBy = useReportControlStore((state) => state.addOrderBy);
  const reportControlData = useReportControlStore((state) => state.reportControlData);
  const controlSettingUI = useControlSettingStore((state) => state.controlSettingUI);
  const reportControlSettingUI = controlSettingUI[EControlSettingType.REPORT];

  const reportAllControlIds = useMemo(
    () => getAllControlIds(reportControlSettingUI),
    [reportControlSettingUI],
  );

  const dimensionIds = useMemo(() => {
    return getControlIdsById(reportControlSettingUI, EReportControlId.DIMENSIONS_FILTER);
  }, [reportControlSettingUI]);

  const filterIds = useMemo(() => {
    return getControlIdsById(reportControlSettingUI, EReportControlId.FILTER);
  }, [reportControlSettingUI]);

  const filters = useMemo(() => {
    return getControlValueById(filterIds, reportControlData);
  }, [reportControlData, filterIds]);

  const metrics = useMemo(() => {
    const dimensions = reportControlSettingUI.find(
      (item) => item.id === EReportControlId.METRICS_FILTER,
    );

    return dimensions?.controls?.map((item) => item.uid).filter((uid) => uid !== undefined) ?? [];
  }, [reportControlSettingUI]);

  const hasCampaign = useMemo(() => {
    if (!reportAllControlIds.has(EReportControlId.TAB_CAMPAIGN)) return false;

    return !!reportControlData?.[EReportControlId.TAB_CAMPAIGN]?.value;
  }, [reportControlData, reportAllControlIds]);

  const dimensions = useMemo(() => {
    return dimensionIds?.map((id) => {
      if (id === EDimensionKey.TIME) {
        const value = reportControlData?.[id]?.value as AnalyticInterval;
        return value;
      }
      return id;
    });
  }, [reportControlData, dimensionIds]);

  const filteredDimensions = useMemo(() => {
    if (hasCampaign) return dimensions;

    return dimensions.filter((item) => item !== EDimensionKey.VERSION);
  }, [dimensions, hasCampaign]);

  const dimensionLookup = useMemo(() => new Set(dimensions), [dimensions]);

  const timeDimension = useMemo(() => dimensions.find((dim) => TIME_KEYS.has(dim)), [dimensions]);

  const data = useMemo(() => {
    const metricKey = reportControlData?.[EReportControlId.METRIC]?.value as IRMetricKey;
    const chartType = reportControlData?.[EReportControlId.CHART]?.value as EReportChart;

    return { metricKey, chartType };
  }, [reportControlData]);

  const onChangeOrderBy = (field: string) => {
    const orderByField = orderBy?.[field];

    if (orderByField) {
      changeOrderBy(
        {
          [field]: {
            ...orderByField,
            direction:
              orderByField.direction === EOrderDirectionType.ASC
                ? EOrderDirectionType.DESC
                : EOrderDirectionType.ASC,
          },
        },
        metrics,
      );
      return;
    }

    changeOrderBy(
      {
        [field]: {
          field,
          direction: EOrderDirectionType.DESC,
        },
      },
      metrics,
    );
  };

  useEffect(() => {
    if (orderBy || !dimensions.length) return;

    const orderByData: OrderByFields = {};
    dimensions.forEach((key) => {
      orderByData[key] = {
        field: key,
        direction: EOrderDirectionType.ASC,
      };
    });
    addOrderBy(orderByData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions]);

  return {
    ...data,
    dimensionLookup,
    dimensions: filteredDimensions,
    metrics,
    orderBy,
    filters,
    onChangeOrderBy,
    timeDimension,
    hasCampaign,
  };
};
